import React from 'react';
import { FileQuestion, Layers, Clock, CheckCircle2, MessageSquare, Users, ArrowRight } from 'lucide-react';
import { WHATSAPP_MESSAGE_URL } from '../constants';

export const ProblemSolutionSection = () => {
  const problems = [
    {
      icon: FileQuestion,
      title: 'Información dispersa',
      description: 'Miembros en Excel, WhatsApp, libretas y diferentes archivos. Sin una fuente única de verdad.',
    },
    {
      icon: Users,
      title: 'Seguimiento difícil',
      description: 'No siempre es fácil saber quién está asistiendo, estudiando o participando activamente.',
    },
    {
      icon: Layers,
      title: 'Formación sin control',
      description: 'Dificultad para conocer el avance de cada persona en su proceso formativo y de discipulado.',
    },
    {
      icon: Clock,
      title: 'Demasiada administración',
      description: 'El equipo pastoral dedicando tiempo a tareas que podrían estar centralizadas y automatizadas.',
    },
  ];

  return (
    <section id="solucion" className="py-20 bg-white dark:bg-slate-900 border-y border-slate-200/60 dark:border-slate-800/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* SECTION HEADER */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">
            ¿Te pasa esto?
          </p>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            Tu iglesia creció.{' '}
            <span className="bg-gradient-to-r from-rose-600 to-orange-600 dark:from-rose-400 dark:to-orange-400 bg-clip-text text-transparent">
              ¿Pero tu forma de organizarla también?
            </span>
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-base font-medium leading-relaxed">
            Cuando la información de tu comunidad no está centralizada, el cuidado pastoral se vuelve disperso y agotador.
          </p>
        </div>

        {/* PROBLEMS GRID */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {problems.map((prob, idx) => {
            const Icon = prob.icon;
            return (
              <div
                key={idx}
                className="bg-slate-50 dark:bg-slate-950 p-6 sm:p-8 rounded-[2rem] border border-slate-200/80 dark:border-slate-800/80 space-y-4 transition-all hover:border-rose-300 dark:hover:border-rose-800 hover:shadow-lg group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-rose-100 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                    <Icon size={24} />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">{prob.title}</h3>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400 leading-relaxed">
                      {prob.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* SOLUTION HIGHLIGHT CARD */}
        <div className="mt-16 bg-gradient-to-br from-indigo-900 via-violet-950 to-slate-900 rounded-[2.5rem] p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-80 h-80 bg-indigo-500/20 blur-[100px] rounded-full" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 text-indigo-200 text-[10px] font-black uppercase tracking-widest backdrop-blur-md">
                La Solución
              </div>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tight leading-snug">
                PastoreApp reúne todo en un solo lugar.
              </h3>
              <p className="text-slate-300 text-sm font-medium leading-relaxed max-w-2xl">
                De la información dispersa a una iglesia organizada. Centraliza personas, formación, grupos, asistencia, liderazgo, finanzas y administración ministerial.
              </p>
              <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-bold text-indigo-100">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400" />
                  <span>Información organizada y disponible siempre</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400" />
                  <span>Seguimiento claro de cada persona</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400" />
                  <span>Control de asistencia y finanzas</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400" />
                  <span>Más tiempo para el trabajo pastoral</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 flex flex-col gap-3">
              <a
                href={WHATSAPP_MESSAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-widest rounded-2xl flex items-center justify-center gap-3 shadow-xl active:scale-95 transition-all"
              >
                <MessageSquare size={16} />
                <span>Solicitar Demo</span>
              </a>
              <a
                href="#plataforma"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#plataforma')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white font-black text-xs uppercase tracking-widest rounded-2xl flex items-center justify-center gap-2 border border-white/20 transition-all"
              >
                <span>Ver la plataforma</span>
                <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
